import React, { useEffect, useRef, useState } from "react";
import Pagination from "./pagination/Pagination";

import styles from "./TableManager.module.css";
import Table from "../table/Table";

import TableManagerHeader from "./table-manager-header/TableManagerHeader";
import { TableManagerContextProvider } from "./TableManagerContext";

import { showPaymentRestrict } from "../restrictionUI/payment/PaymentRestrict";
import { showPageRestrict } from "../restrictionUI/page/PageRestrict";
import { generateNewColumns } from "./TableMeta/NewColumnGenerator";

const DEFAULT_IS_RECORD_SELECTABLE = () => true;

/**
 * TableManager component
 *
 * @param {Boolean} clientSide - When true, all filter, search, and sort
 *   operations are handled in-memory on the fetched records. No server call
 *   is made when the user searches, sorts, or filters. Defaults to false.
 *
 * @param {Array} columns - Array of column objects
 * @param {Object} filterConfig - Table filter configuration.
 * @param {Object} filterConfig.active_filters - Optional filters to apply
 *   before the first table request. Use `quick_filters` and
 *   `comprehensive_filters` arrays with `{ column, value }` entries.
 * @param {string|number} filterConfig.activeFiltersKey - Optional key used to
 *   re-apply active filters after the table is already mounted. Increment or
 *   change this value when applying a new external filter.
 *
 * Filter precedence is column-wise:
 * 1. `active_filters`
 * 2. options marked with `initially_active: true`
 * 3. existing TableManager defaults, such as quick filter "All"
 *
 * @example
 * const filterConfig = {
 *   active_filters: {
 *     quick_filters: [
 *       { column: "Last & Days", value: ["last7"] },
 *     ],
 *     comprehensive_filters: [
 *       { column: "platform", value: ["Windows", "MAC"] },
 *     ],
 *   },
 *   activeFiltersKey: Date.now(),
 *   comprehensive_filters: [
 *     {
 *       heading: "Platform",
 *       column: "platform",
 *       type: "multiple",
 *       options: [
 *         { label: "Windows", value: "Windows", initially_active: true },
 *         { label: "MAC", value: "MAC" },
 *       ],
 *     },
 *   ],
 *   quick_filters: [
 *     {
 *       column: "patchFamily",
 *       options: [
 *         { label: "All Patches", value: ["ALL"], initially_active: true },
 *         { label: "OS", value: ["OS"] },
 *       ],
 *     },
 *   ],
 * };
 *
 * <TableManager
 *   headingText="Total Devices : "
 *   url="/devices/get-scan"
 *   columns={columns}
 *   filterConfig={filterConfig}
 * />
 *
 * @param {String} url - URL to fetch records from
 * @param reqMethod
 * @param directRecords
 * @param refreshKey
 * @param onBackButtonClick
 * @param {String} headingText - Heading text for the table
 * @param selectable
 * @param recordsToExclude
 * @param uniqueAccessor
 * @param onSelectRecordCallback
 * @param {Object} containerProps - Props to be passed to the container div
 * @param bulkOptionsConfig
 * @param actionsConfig
 * @param searchNeeded
 * @param exportNeeded
 * @param columChooseNeeded
 * @param searchControlNeeded
 * @param AlternateComponent
 * @param {Boolean} alternateOnEmpty - Render AlternateComponent when a server-backed table has zero results.
 * @param setResult
 * @param ExtraComponent
 * @param extraComponentProperties
 * @param setSelectedRows
 * @param customeClearSelectedRowsRef
 * @param columnResizeNeeded
 * @param recordsToDisable
 * @param alreadySelectedIds
 * @param clientSide - it handles all filter, search, and sort operations in-memory on the fetched records. No server call is made when the user searches, sorts, or filters. Defaults to false.
 * @param {Object} rest - Rest of the props to be passed to the Table component
 */
const TableManager = ({
  columns,
  filterConfig,
  url = "",
  reqMethod,
  defaultPageSize,
  pageSizeOptions = [10, 25, 50, 100, 150],
  directRecords,
  total: propTotal,
  refreshKey,
  onBackButtonClick,
  headingText,
  selectable,
  recordsToExclude,
  uniqueAccessor,
  onSelectRecordCallback,
  containerProps,
  headerProps,
  paginationContainerProps,
  bulkOptionsConfig,
  actionsConfig,
  searchNeeded,
  exportNeeded,
  columChooseNeeded,
  searchControlNeeded = true,
  clientSide = false, // single flag for in-memory filter + search + sort
  paginationNeeded = true,
  alternateOnEmpty = false,
  AlternateComponent = () => <div>No Records Present</div>,
  setResult,
  ExtraComponent = () => <div></div>,
  extraComponentProperties = {
    hideOnNoSelect: true,
    position: "left",
  },
  setSelectedRows,
  tooltipConfig = {
    enabled: true,
    hideOnClick: false,
  },
  customeClearSelectedRowsRef,
  columnResizeNeeded = true,
  recordsToDisable = [],
  isRecordSelectable = DEFAULT_IS_RECORD_SELECTABLE,
  alreadySelectedIds = null, //to indicate selected records in table, pass the records which are selected in array of json as per the record structure of the table... then only it works
  filterComponents = [],
  apiClient,
  ...rest
}) => {
  const tableRef = useRef(null);
  const [total, setTotal] = useState(null);
  const [visitedRecords, setVisitedRecords] = useState([]); //for storing all the records which are visited by the users
  const [isRestricted, setIsRestricted] = useState(false);
  const [payment, isPayment] = useState(false);
  const initialClearSelectedRowRef = useRef();
  const clearSelectedRowsRef = customeClearSelectedRowsRef
    ? customeClearSelectedRowsRef
    : initialClearSelectedRowRef;

  // When directRecords and propTotal are provided, use propTotal instead of counting records
  useEffect(() => {
    if (directRecords !== undefined && propTotal !== undefined) {
      setTotal(propTotal);
    }
  }, [directRecords, propTotal]);

  if (payment) setTimeout(() => showPaymentRestrict(true), 100);
  if (isRestricted && !payment) setTimeout(() => showPageRestrict(), 100);

  const [apiColumns, setApiColumns] = useState([]);
  const effectiveColumns = apiColumns.length > 0 ? apiColumns : columns;
  const newColumns = generateNewColumns(columns, effectiveColumns);

  const [apiFilterConfig, setApiFilterConfig] = useState(null);
  const newFilterConfig = apiFilterConfig ? apiFilterConfig : filterConfig;

  return (
    <TableManagerContextProvider
      {...{
        columns: newColumns,
        url,
        reqMethod,
        directRecords,
        propTotal,
        refreshKey,
        filterConfig: newFilterConfig,
        selectable,
        recordsToExclude,
        uniqueAccessor,
        onSelectRecordCallback,
        bulkOptionsConfig,
        actionsConfig,
        searchNeeded,
        exportNeeded,
        columChooseNeeded,
        setTotal,
        setResult,
        ExtraComponent,
        extraComponentProperties,
        setSelectedRows,
        setIsRestricted,
        isPayment,
        clearSelectedRowsRef,
        columnResizeNeeded,
        alreadySelectedIds,
        visitedRecords,
        setVisitedRecords,
        searchControlNeeded,
        clientSide,
        paginationNeeded,
        defaultPageSize: defaultPageSize,
        setApiColumns,
        setApiFilterConfig,
        apiClient,
      }}
    >
      {total >= 0 &&
        !(alternateOnEmpty && total === 0) &&
        directRecords === undefined && (
          <div
            id="table-manager"
            className={styles.tableContainer}
            {...containerProps}
          >
            <TableManagerHeader
              headingText={headingText}
              onBackButtonClick={onBackButtonClick}
              ExtraComponent={ExtraComponent}
              extraComponentProperties={extraComponentProperties}
              clearSelectedRowsRef={clearSelectedRowsRef}
              searchControlNeeded={searchControlNeeded}
              headerProps={headerProps}
              filterComponents={filterComponents}
            />
            <div
              id="table-container"
              className={[styles.tableWrapper, "scrollBarOnHover"]
                .filter(Boolean)
                .join(" ")}
              ref={tableRef}
            >
              <Table
                {...rest}
                setSelectedRows={setSelectedRows}
                clearSelectedRowsRef={clearSelectedRowsRef}
                columnResizeNeeded={columnResizeNeeded}
                total={total}
                alreadySelectedIds={alreadySelectedIds}
                visitedRecords={visitedRecords}
                recordsToDisable={recordsToDisable}
                isRecordSelectable={isRecordSelectable}
                tooltipConfig={tooltipConfig}
              />
            </div>
            {paginationNeeded && (
              <Pagination
                defaultSize={defaultPageSize}
                pageSizeOptions={pageSizeOptions}
                paginationContainerProps={paginationContainerProps}
              />
            )}
          </div>
        )}

      {(total === -1 ||
        (alternateOnEmpty && total === 0 && directRecords === undefined)) && (
        <div
          id="table-manager"
          className={styles.tableContainer}
          style={{ minHeight: "100%", height: "auto" }}
          {...containerProps}
        >
          <AlternateComponent />
        </div>
      )}

      {directRecords !== undefined && (
        <div
          id="table-manager"
          className={styles.tableContainer}
          {...containerProps}
        >
          <TableManagerHeader
            headingText={headingText}
            onBackButtonClick={onBackButtonClick}
            headerProps={headerProps}
          />
          <div
            id="table-container"
            className={[styles.tableWrapper, "scrollBarOnHover"].join(" ")}
            ref={tableRef}
          >
            <Table {...rest} isRecordSelectable={isRecordSelectable} />
          </div>
          {paginationNeeded && (
            <Pagination
              defaultSize={defaultPageSize}
              pageSizeOptions={pageSizeOptions}
              paginationContainerProps={paginationContainerProps}
            />
          )}
        </div>
      )}

      {total === -2 && (
        <div
          style={{ paddingTop: "3.5rem" }}
          id="table-manager"
          className={styles.tableContainer}
          {...containerProps}
        >
          <div style={{ display: "none" }}>
            <TableManagerHeader
              headingText={headingText}
              headerProps={headerProps}
            />
          </div>
          <div
            id="table-container"
            className={[styles.tableWrapper, "scrollBarOnHover"].join(" ")}
            ref={tableRef}
          >
            <Table
              {...rest}
              setSelectedRows={setSelectedRows}
              clearSelectedRowsRef={clearSelectedRowsRef}
              isRecordSelectable={isRecordSelectable}
            />
          </div>
        </div>
      )}
    </TableManagerContextProvider>
  );
};

export default TableManager;
